import { useMemo } from 'react';
import useSWR, { SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';

export type UserPnlInterval = '24h' | '1w' | '1m' | '1y' | 'max';

export interface PnlData {
  pnl: number;
  pnlPercent: number;
  currentValue: number;
  cash: number;
}
export interface UserPnlData extends PnlData {
  interval: UserPnlInterval;
  field: 'pnl_1d_usd' | 'pnl_7d_usd' | 'pnl_30d_usd' | 'pnl_365d_usd';
  detail: Record<string, PnlData>;
}

export interface UserPnlResponse {
  success: boolean;
  data: UserPnlData;
  error?: string;
}

export interface UseUserPnlOptions {
  wallets?: string[];
  interval?: UserPnlInterval;
  enabled?: boolean;
  swr?: SWRConfiguration<UserPnlData, Error>;
}

const DEFAULT_INTERVAL: UserPnlInterval = '24h';

export async function fetchUserPnl(
  wallets: string[],
  interval: UserPnlInterval = DEFAULT_INTERVAL,
) {
  const response = await customFetch('/api/market/userpnl', {
    method: 'POST',
    body: JSON.stringify({
      wallets,
      interval,
    }),
  });

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch user pnl');
  }

  const result = (await response.json()) as UserPnlResponse;

  if (!result.success) {
    throw new Error(result.error || 'Failed to fetch user pnl');
  }

  return result.data;
}

export function useUserPnl(options: UseUserPnlOptions = {}) {
  const wallets = useMemo(() => normalizeWallets(options.wallets ?? []), [options.wallets]);
  const interval = options.interval ?? DEFAULT_INTERVAL;
  const enabled = options.enabled ?? true;

  const swrKey = enabled && wallets.length > 0 ? ['user-pnl', wallets.join(','), interval] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<UserPnlData, Error>(
    swrKey,
    () => fetchUserPnl(wallets, interval),
    {
      revalidateOnFocus: false,
      ...options.swr,
    },
  );

  return {
    data: data ?? null,
    pnl: data?.pnl ?? 0,
    pnlPercent: data?.pnlPercent ?? 0,
    currentValue: data?.currentValue ?? 0,
    cash: data?.cash ?? 0,
    interval,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeWallets(wallets: string[]) {
  return [...new Set(wallets.map((wallet) => wallet.trim().toLowerCase()).filter(Boolean))];
}

export default useUserPnl;
